import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Send, Wallet, AlertCircle, CheckCircle2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn, formatMoney } from '@/lib/utils';
import { ApiError } from '@/api/client';
import { useDialogA11y } from '@/hooks/useDialogA11y';
import {
  usePispiAliasLookup,
  useSendPispiRtp,
} from '@/api/queries/pispi';

// ─────────────────────────────────────────────────────────────
// UUID v4 validation (loose: any RFC-4122 UUID, case-insensitive)
// ─────────────────────────────────────────────────────────────

const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function isValidUuid(value: string): boolean {
  return UUID_REGEX.test(value.trim());
}

// ─────────────────────────────────────────────────────────────
// Debounce hook
// ─────────────────────────────────────────────────────────────

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState<T>(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

// ─────────────────────────────────────────────────────────────
// Props
// ─────────────────────────────────────────────────────────────

export interface PispiRtpDialogInvoice {
  id: string;
  number: string;
  /** What the client still owes, as the server computed it (`remainingDue`). */
  remainingDue: number;
  /** Stored currency of the invoice: the currency of `remainingDue`. */
  currency: string;
  dueDate?: string | null | undefined;
}

export interface PispiRtpDialogProps {
  isOpen: boolean;
  onClose: () => void;
  invoice: PispiRtpDialogInvoice;
}

// ─────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────

export function PispiRtpDialog({ isOpen, onClose, invoice }: PispiRtpDialogProps) {
  const [alias, setAlias] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);

  const debouncedAlias = useDebouncedValue(alias.trim(), 500);
  const isAliasFormatValid = debouncedAlias.length > 0 && isValidUuid(debouncedAlias);

  const lookup = usePispiAliasLookup(debouncedAlias, {
    enabled: isOpen && isAliasFormatValid,
  });

  const sendRtp = useSendPispiRtp();

  const remainingAmount = Math.max(invoice.remainingDue, 0);

  const handleClose = () => {
    if (sendRtp.isPending) {
      toast.info('Opération en cours, veuillez patienter');
      return;
    }
    onClose();
  };

  const aliasRef = useRef<HTMLInputElement>(null);
  const dialogRef = useDialogA11y<HTMLDivElement>({
    open: isOpen,
    onClose: handleClose,
    initialFocus: aliasRef,
  });

  // Reset dialog state whenever it reopens
  useEffect(() => {
    if (isOpen) {
      setAlias('');
      setSubmitError(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const lookupNotFound =
    !!debouncedAlias &&
    isAliasFormatValid &&
    lookup.isError &&
    lookup.error instanceof ApiError &&
    lookup.error.isNotFound();

  const lookupBadRequest =
    !!debouncedAlias &&
    isAliasFormatValid &&
    lookup.isError &&
    lookup.error instanceof ApiError &&
    lookup.error.status === 400;

  const lookupUnknownError =
    !!debouncedAlias &&
    isAliasFormatValid &&
    lookup.isError &&
    !lookupNotFound &&
    !lookupBadRequest;

  const hasClient = !!lookup.data && !lookup.isError;
  const canSubmit =
    isAliasFormatValid && hasClient && !sendRtp.isPending && !lookup.isFetching;

  const showFormatHint =
    debouncedAlias.length > 0 && !isAliasFormatValid;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitError(null);

    const dateLimitePaiement =
      invoice.dueDate && invoice.dueDate.length > 0
        ? new Date(invoice.dueDate).toISOString()
        : new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString();

    try {
      await sendRtp.mutateAsync({
        invoiceId: invoice.id,
        payeurAlias: debouncedAlias,
        dateLimitePaiement,
        motif: `Facture ${invoice.number}`,
        confirmation: false,
      });
      toast.success('Demande de paiement envoyée');
      onClose();
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : "Erreur lors de l'envoi de la demande";
      setSubmitError(message);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
      />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="pispi-rtp-dialog-title"
        className="relative bg-white rounded-2xl shadow-xl max-w-lg w-full mx-4 p-6"
      >
        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
            <Wallet className="w-5 h-5 text-indigo-700" />
          </div>
          <div>
            <h3 id="pispi-rtp-dialog-title" className="text-lg font-semibold text-slate-900">
              Demande de paiement PI-SPI
            </h3>
            <p className="text-sm text-slate-500">
              Facture {invoice.number} — Solde à payer:{' '}
              <span className="font-medium text-slate-700">
                {formatMoney(remainingAmount, invoice.currency)}
              </span>
            </p>
          </div>
        </div>

        {/* Alias input */}
        <div className="space-y-4 mb-4">
          <div>
            <Label htmlFor="pispi-alias" className="mb-2 block">
              Alias du client (UUID)
            </Label>
            <Input
              ref={aliasRef}
              id="pispi-alias"
              type="text"
              value={alias}
              onChange={(e) => setAlias(e.target.value)}
              placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
              autoComplete="off"
              spellCheck={false}
              disabled={sendRtp.isPending}
            />
            {showFormatHint && (
              <p className="mt-1 text-xs text-slate-500">
                Format attendu: UUID (ex: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx)
              </p>
            )}
          </div>

          {/* Lookup states */}
          {isAliasFormatValid && lookup.isFetching && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-slate-50 border border-slate-200 text-slate-600 text-sm">
              <Loader2 className="w-4 h-4 animate-spin" />
              Vérification de l'alias...
            </div>
          )}

          {isAliasFormatValid && !lookup.isFetching && hasClient && lookup.data && (
            <div className="p-4 rounded-lg bg-emerald-50 border border-emerald-200">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-emerald-700 mt-0.5 shrink-0" />
                <div className="min-w-0">
                  <p className="font-semibold text-slate-900 truncate">
                    {lookup.data.client.nom}
                  </p>
                  <p className="text-sm text-slate-600">
                    {lookup.data.client.paysResidence}
                    {lookup.data.client.ville
                      ? ` — ${lookup.data.client.ville}`
                      : ''}
                  </p>
                  {lookup.data.client.categorie && (
                    <p className="text-xs text-slate-500 mt-1">
                      Catégorie: {lookup.data.client.categorie}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {isAliasFormatValid && !lookup.isFetching && lookupNotFound && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>Alias introuvable</span>
            </div>
          )}

          {isAliasFormatValid && !lookup.isFetching && lookupBadRequest && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>Alias invalide</span>
            </div>
          )}

          {isAliasFormatValid && !lookup.isFetching && lookupUnknownError && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>
                {lookup.error instanceof Error
                  ? lookup.error.message
                  : "Erreur lors de la vérification de l'alias"}
              </span>
            </div>
          )}

          {submitError && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{submitError}</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={sendRtp.isPending}
          >
            Annuler
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className={cn('bg-indigo-700 hover:bg-indigo-800 text-white')}
          >
            {sendRtp.isPending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Envoi en cours...
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                Confirmer
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default PispiRtpDialog;
